import { supabase } from '@/integrations/supabase/client';
import { startOfDayBrazilISO } from '@/lib/brazilTime';

export interface ModoForjaSession {
  id: string;
  user_id: string;
  session_type: 'focus' | 'break';
  started_at: string;
  ended_at: string | null;
  duration_seconds: number | null;
  planned_duration_seconds: number;
  completed: boolean;
}

export interface ModoForjaMetrics {
  sessions_today: number;
  active_users_today: number;
  total_seconds_all_time: number;
  total_seconds_today: number;
  total_seconds_yesterday: number;
  sessions_yesterday: number;
}

// Start a new session
export async function startSession(
  sessionType: 'focus' | 'break',
  plannedDurationSeconds: number
): Promise<string | null> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;

  const { data, error } = await supabase
    .from('modoforja_sessions')
    .insert({
      user_id: user.id,
      session_type: sessionType,
      planned_duration_seconds: plannedDurationSeconds,
      started_at: new Date().toISOString(),
    })
    .select('id')
    .single();

  if (error) {
    console.error('Error starting session:', error);
    return null;
  }

  return data?.id || null;
}

// End a session and record duration
export async function endSession(
  sessionId: string,
  durationSeconds: number,
  completed: boolean
): Promise<boolean> {
  const { error } = await supabase
    .from('modoforja_sessions')
    .update({
      ended_at: new Date().toISOString(),
      duration_seconds: durationSeconds,
      completed,
    })
    .eq('id', sessionId);

  if (error) {
    console.error('Error ending session:', error);
    return false;
  }

  return true;
}

// Get metrics for admin panel
export async function fetchModoForjaMetrics(): Promise<ModoForjaMetrics | null> {
  const { data, error } = await supabase
    .from('modoforja_metrics')
    .select('*')
    .single();

  if (error) {
    console.error('Error fetching metrics:', error);
    return null;
  }

  return data as ModoForjaMetrics;
}

// Get user's sessions for today
export async function fetchUserSessionsToday(): Promise<number> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return 0;

  const todayISO = startOfDayBrazilISO();

  const { count, error } = await supabase
    .from('modoforja_sessions')
    .select('*', { count: 'exact', head: true })
    .eq('user_id', user.id)
    .eq('session_type', 'focus')
    .eq('completed', true)
    .gte('started_at', todayISO);

  if (error) {
    console.error('Error fetching user sessions:', error);
    return 0;
  }

  return count || 0;
}

// Format seconds to human readable time
export function formatDuration(totalSeconds: number): string {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
}
